import posthog from 'posthog-js'

/**
 * «هدف صندوق» — asked once, when a box is created.
 *
 * Until this existed the only clue to why someone built a box was its name,
 * and a third of names say nothing («صندوق ۱», «همیان»). Follow-up calls to
 * new managers were guessing. The answer is stored on the box (`goal`,
 * `goal_description`) and is write-only on the backend: nothing reads it back
 * into the app, so it can never change what a member sees.
 *
 * The codes are the backend's `choice.BOX_GOAL`; the server rejects anything
 * else, so keep the two lists identical.
 */
export type FundGoal =
    | 'fam'
    | 'frn'
    | 'clg'
    | 'rlg'
    | 'nbr'
    | 'chr'
    | 'prc'
    | 'sav'
    | 'bsn'
    | 'oth'

export const FUND_GOALS: Array<{ value: FundGoal; label: string }> = [
    { value: 'fam', label: 'خانوادگی و فامیلی' },
    { value: 'frn', label: 'دوستانه' },
    { value: 'clg', label: 'همکاران یا صنفی' },
    { value: 'rlg', label: 'هیئت، مسجد یا کانون مذهبی' },
    { value: 'nbr', label: 'محله یا ساختمان' },
    { value: 'chr', label: 'کار خیر و کمک به نیازمندان' },
    { value: 'prc', label: 'پس‌انداز برای خرید مشخص (جهیزیه، مسکن، سفر)' },
    { value: 'sav', label: 'پس‌انداز شخصی' },
    { value: 'bsn', label: 'سرمایهٔ کسب‌وکار' },
    { value: 'oth', label: 'سایر' },
]

export const FUND_GOAL_QUESTION = 'هدف شما از ساخت این صندوق چیست؟'
export const FUND_GOAL_REQUIRED = 'هدف صندوق را انتخاب کنید'
export const FUND_GOAL_DESCRIPTION_MAX = 200

export default function FundGoalPicker({
    goal,
    description,
    onGoalChange,
    onDescriptionChange,
    boxKind,
    error,
}: {
    goal: FundGoal | ''
    description: string
    onGoalChange: (goal: FundGoal) => void
    onDescriptionChange: (description: string) => void
    /** which creation flow asked, so one PostHog event serves both */
    boxKind: 'moneypool' | 'cashbox'
    error?: string
}) {
    return (
        <div>
            <div className="flex flex-wrap gap-2">
                {FUND_GOALS.map((option) => {
                    const selected = goal === option.value
                    return (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => {
                                if (selected) return
                                onGoalChange(option.value)
                                posthog.capture('fund_goal_selected', {
                                    goal: option.value,
                                    box_kind: boxKind,
                                    changed: goal !== '',
                                })
                            }}
                            className={`rounded-full border px-3 py-2 text-sm leading-5 text-right ${
                                selected
                                    ? 'border-green-1 bg-[#E6F3DD] text-green-1 font-bold'
                                    : 'border-[#E0E0E0] bg-white text-text-body'
                            }`}
                        >
                            {option.label}
                        </button>
                    )
                })}
            </div>
            {error && <p className="text-xs text-[#B3261E] mt-2">{error}</p>}
            {goal !== '' && (
                <div className="mt-3">
                    <p className="text-sm text-text-body mb-1.5">
                        {goal === 'oth' ? 'هدفتان را بنویسید' : 'توضیح بیشتر'}
                        <span className="text-text-secondary mx-1">
                            (اختیاری)
                        </span>
                    </p>
                    <textarea
                        value={description}
                        maxLength={FUND_GOAL_DESCRIPTION_MAX}
                        rows={2}
                        placeholder={
                            goal === 'oth'
                                ? 'مثلاً: جمع کردن هزینهٔ درمان یکی از اعضا'
                                : 'مثلاً: برای کمک به جهیزیهٔ دخترهای فامیل'
                        }
                        onChange={(event) =>
                            onDescriptionChange(event.target.value)
                        }
                        className="w-full rounded-[10px] border border-[#E0E0E0] bg-white px-3 py-2 text-sm leading-6 outline-none focus:border-green-1 resize-none"
                    />
                </div>
            )}
        </div>
    )
}
